// Public registration API client.
// Calls api2.paworld365.com directly (CORS-enabled public endpoints).
import type {
  PublicEvent,
  PublicOrder,
  PaymentLinkResponse,
  InvoiceDownloadResponse,
  RegistrationDetail,
  CountryOption,
  OrderPreviewInput,
  OrderPreviewResponse,
  OrderConfirmResponse,
} from "./types";
import { isPricingRowActiveAt } from "@/lib/pricing/pricingWindow";
import { assertCheckoutMutationAllowed } from "./productionMode";
import { getApiAccessToken } from "@/lib/auth/apiAuth";
import { ACCOUNT_ORDER_CLAIM_ENABLED } from "@/lib/account/orderClaimFlag";

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "https://api2.paworld365.com/api").replace(/\/+$/, "");

async function getEventHomeExtras(slug: string): Promise<{ accommodationCta?: { label: string; url: string } | null } | null> {
  try {
    const res = await fetch(`${API_BASE}/public-content/events/${encodeURIComponent(slug)}/home`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const data = await res.json();
    const cta = data?.venue?.accommodationCta;
    if (cta && typeof cta.label === "string" && typeof cta.url === "string" && cta.label.trim() && cta.url.trim()) {
      return { accommodationCta: { label: cta.label, url: cta.url } };
    }
    return null;
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  correlationId?: string;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
    this.correlationId = (body as any)?.correlationId;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = String(init?.method ?? "GET").toUpperCase();
  if (method !== "GET" && method !== "HEAD") {
    // Runtime defence in depth: rollback must stay read-only even if a stale or
    // hidden component reaches an API client directly.
    assertCheckoutMutationAllowed(`${method} ${path}`);
  }
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  let body: any = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = text; }
  if (!res.ok) {
    const msg = (body && (body.error || body.message)) || res.statusText;
    throw new ApiError(res.status, body, msg);
  }
  return body as T;
}

/**
 * A magic-link bearer remains authoritative while it is present. After CIAM
 * returns to a token-free URL, fall back to the signed-in API bearer held by
 * MSAL's in-memory bridge. Neither token is copied into browser storage.
 */
async function orderAccessHeaders(
  magicToken?: string,
): Promise<Record<string, string>> {
  const token =
    magicToken ||
    (ACCOUNT_ORDER_CLAIM_ENABLED ? await getApiAccessToken() : null);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ---------- Events ----------
export async function getEvent(slug: string): Promise<PublicEvent | null> {
  try {
    const [data, extras] = await Promise.all([
      request<any>(`/public/events/${encodeURIComponent(slug)}`),
      getEventHomeExtras(slug),
    ]);
    if (!data || typeof data !== "object") return null;

    if (data.event && typeof data.event === "object") {
      // One display/selection clock for this complete mapping pass. The backend
      // remains quote authority; this only keeps stale/future rows out of the
      // currently selectable registration options.
      const nowMs = Date.now();
      const ev = data.event;
      const rawPasses = Array.isArray(data.pricing?.passes) ? data.pricing.passes : [];
      // Match pricingToTeaser's deterministic pass order so a bare /register
      // fallback and the Event page's preferred CTA/card choose the same row.
      // Stable sort preserves API order when displayOrder ties or is absent;
      // each pass's price-array order is preserved below.
      const passes = [...rawPasses].sort(
        (a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999),
      );
      const defaultCurrency = String(data.pricing?.currency ?? "USD");
      const offerings: PublicEvent["offerings"] = [];
      for (const p of passes) {
        const passName = String(p.passName ?? p.name ?? "Pass");
        const passDesc = typeof p.passDescription === "string" ? p.passDescription : (typeof p.description === "string" ? p.description : "");
        const prices = Array.isArray(p.prices) ? p.prices : null;
        if (prices && prices.length) {
          for (const pr of prices) {
            const id = String(pr.offeringPublicId ?? "");
            if (!id) continue;
            const available = pr.available !== false;
            const validFrom =
              typeof pr.validFrom === "string" || pr.validFrom === null
                ? pr.validFrom
                : undefined;
            const validUntil =
              typeof pr.validUntil === "string" || pr.validUntil === null
                ? pr.validUntil
                : undefined;
            if (!isPricingRowActiveAt({ available, validFrom, validUntil }, nowMs)) continue;
            const priceLabel = typeof pr.priceLabel === "string" && pr.priceLabel.trim() ? pr.priceLabel : "Standard";
            offerings.push({
              id,
              priceBookEntryId: pr.priceBookEntryPublicId ? String(pr.priceBookEntryPublicId) : undefined,
              available,
              validFrom,
              validUntil,
              isDefault: pr.isDefault === true,
              name: `${passName} · ${priceLabel}`,
              productLabel: passName,
              priceLabel,
              description: passDesc,
              unitPriceNet: Number(pr.amount ?? 0),
              currency: String(pr.currency ?? defaultCurrency),
            });
          }
        } else {
          const id = String(p.publicId ?? p.id ?? "");
          if (!id) continue;
          offerings.push({
            id,
            available: p.available !== false,
            validFrom:
              typeof p.validFrom === "string" || p.validFrom === null
                ? p.validFrom
                : undefined,
            validUntil:
              typeof p.validUntil === "string" || p.validUntil === null
                ? p.validUntil
                : undefined,
            isDefault: p.isDefault === true,
            name: passName,
            description: passDesc,
            unitPriceNet: Number(p.amount ?? p.unitPriceNet ?? 0),
            currency: String(p.currency ?? defaultCurrency),
          });
        }
      }
      return {
        slug: String(ev.slug ?? slug),
        name: String(ev.name ?? ""),
        description: String(ev.description ?? ""),
        heroImageUrl: ev.heroImageUrl ?? null,
        startDate: String(ev.startDate ?? ""),
        endDate: String(ev.endDate ?? ""),
        city: String(ev.city ?? ""),
        country: String(ev.country ?? ""),
        venue: ev.venue ?? null,
        accommodationCta: extras?.accommodationCta ?? null,
        marketingUrl: ev.marketingUrl ?? null,
        offerings,
      };
    }

    if (typeof data.slug === "string") return data as PublicEvent;
    return null;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

/**
 * Read-only fetch of the public Event response's raw `pricing` block.
 *
 * The live `/public/events/{slug}` response carries the commercial pricing
 * (state + passes/prices) even when the content microsite's home payload does
 * not. The content provider uses this as a narrow fallback so the Event page
 * teaser can show live passes/prices without a second frontend money model.
 * Returns the raw pricing object, or null on any failure / when absent.
 */
export async function getPublicEventPricingRaw(slug: string): Promise<unknown> {
  try {
    const data = await request<unknown>(`/public/events/${encodeURIComponent(slug)}`);
    if (data && typeof data === "object" && "pricing" in data) {
      const pricing = (data as { pricing?: unknown }).pricing;
      return pricing && typeof pricing === "object" ? pricing : null;
    }
    return null;
  } catch {
    return null;
  }
}

// ---------- Coupons ----------
export type DiscountKind = "percent" | "fixed_cart" | "fixed_per_item";

export interface CouponValidation {
  valid: boolean;
  code?: string;
  discountType?: "percent" | "fixed_cart" | "fixed_product" | string;
  discountKind?: DiscountKind | string;
  discountAmount?: number;
  discountSummary?: string;
  error?: string;
}

export async function validateCoupon(
  code: string,
  offeringId: string,
  eventSlug: string,
): Promise<CouponValidation> {
  try {
    const params = new URLSearchParams({ code, eventSlug });
    // Send the public offering ID (e.g. "of_..."). The backend rejects raw
    // Dataverse GUIDs, so we send anything non-GUID-shaped.
    if (offeringId && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(offeringId)) {
      params.set("offeringId", offeringId);
    }
    const res = await fetch(`${API_BASE}/public/coupons/validate?${params.toString()}`, {
      headers: { Accept: "application/json" },
    });
    const text = await res.text();
    let body: any = null;
    try { body = text ? JSON.parse(text) : null; } catch { body = null; }
    if (!res.ok) {
      return { valid: false, code, error: body?.error || "Could not verify coupon. Try again." };
    }
    if (!body || typeof body !== "object" || typeof body.valid !== "boolean") {
      return { valid: false, code, error: "Could not verify coupon. Try again." };
    }
    return body as CouponValidation;
  } catch {
    return { valid: false, code, error: "Could not verify coupon. Try again." };
  }
}

// ---------- Orders ----------
export interface CreateOrderInput {
  orderKey: string;
  turnstileToken: string;
  buyer: {
    firstName: string;
    lastName: string;
    email: string;
    isCompany: boolean;
    companyName?: string;
    vatNumber?: string;
    billingCountry: string;
    billingPostcode?: string;
  };
  lineItem: { eventSlug: string; offeringId: string; priceBookEntryId?: string; quantity: number };
  couponCode?: string;
  referralSource?: string;
}

export interface CreateOrderResponse {
  orderKey: string;
  orderId?: string;
  paymentUrl: string;
}

export async function createOrder(input: CreateOrderInput): Promise<CreateOrderResponse> {
  return request<CreateOrderResponse>("/public/orders", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function getOrder(orderKey: string, authToken?: string): Promise<PublicOrder> {
  return request<PublicOrder>(`/public/orders/${encodeURIComponent(orderKey)}`, {
    headers: await orderAccessHeaders(authToken),
  });
}

export async function resendRegistrationVerificationEmail(
  orderKey: string,
  registrationPublicId: string,
  authToken?: string,
): Promise<{ status: 202 | 409 | 429 }> {
  const response = await fetch(
    `${API_BASE}/public/orders/${encodeURIComponent(orderKey)}/registrations/${encodeURIComponent(registrationPublicId)}/verification-email`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        ...(await orderAccessHeaders(authToken)),
      },
    },
  );
  if (response.status === 202 || response.status === 409 || response.status === 429) {
    return { status: response.status };
  }
  throw new ApiError(response.status, null, "Unable to resend confirmation email");
}

export async function requestPaymentLink(
  orderKey: string,
  authToken?: string,
): Promise<PaymentLinkResponse> {
  return request<PaymentLinkResponse>(
    `/public/orders/${encodeURIComponent(orderKey)}/payment-link`,
    {
      method: "POST",
      headers: await orderAccessHeaders(authToken),
      body: "{}",
    },
  );
}

/**
 * A returned download link is only ever opened when it is an absolute https
 * URL. Rejects "", relative paths, javascript:/data: and any non-https scheme,
 * so a malformed or hostile response can never be handed to window.open.
 */
export function isSafeInvoiceDownloadUrl(value: unknown): value is string {
  if (typeof value !== "string" || !value.trim()) return false;
  let u: URL;
  try {
    u = new URL(value.trim());
  } catch {
    return false;
  }
  return u.protocol === "https:";
}

/** Customer-safe pending copy from the backend, when present. */
function pendingMessageOf(body: any): string | null {
  const m = body?.message;
  if (typeof m !== "string" || !m.trim()) return null;
  const message = m.trim();
  // Old deployments promised an email/24-hour outcome that the current
  // contract does not guarantee. Suppress it and let the caller use bounded copy.
  if (/\b24[ -]?hours?\b/i.test(message) || /\b(sent|send).*\bemail\b/i.test(message)) {
    return null;
  }
  return message;
}

export async function downloadInvoice(
  orderKey: string,
  authToken?: string,
): Promise<InvoiceDownloadResponse> {
  const res = await fetch(
    `${API_BASE}/public/orders/${encodeURIComponent(orderKey)}/invoice/download`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        ...(await orderAccessHeaders(authToken)),
      },
      redirect: "manual",
    },
  ).catch((e) => { throw e; });

  // 202 — generation in progress. Retain the backend's "please wait" copy.
  if (res.status === 202) {
    let body: any = null;
    try { body = await res.json(); } catch { /* ignore */ }
    return {
      status: "pending",
      invoiceStatus: body?.invoiceStatus ?? body?.status ?? null,
      correlationId: body?.correlationId ?? null,
      message: pendingMessageOf(body),
    };
  }

  if (res.status >= 300 && res.status < 400) {
    const loc = res.headers.get("Location");
    if (isSafeInvoiceDownloadUrl(loc)) return { status: "ready", url: loc.trim() };
  }

  if (!res.ok && res.status !== 0) {
    let body: any = null;
    try { body = await res.json(); } catch { /* ignore */ }
    throw new ApiError(res.status, body, body?.error || body?.message || res.statusText);
  }

  let body: any = null;
  try { body = await res.json(); } catch { /* ignore */ }

  // DEV-401 soft 200 — no Invoice row yet on a never-block Order:
  //   { pending: true, message: "Your invoice will be sent by email." }
  // This is a success response, NOT an error, and its message is the copy the
  // customer must see. Checked before the URL branch so a body carrying both
  // can never be mistaken for a ready download.
  if (body?.pending === true) {
    return {
      status: "pending",
      invoiceStatus: body?.invoiceStatus ?? null,
      correlationId: body?.correlationId ?? null,
      message: pendingMessageOf(body),
    };
  }

  const url = body?.downloadUrl ?? body?.url ?? (typeof body === "string" ? body : null);
  if (isSafeInvoiceDownloadUrl(url)) return { status: "ready", url: url.trim() };

  // A URL-shaped field that is present but unusable is a contract violation —
  // surface it rather than silently opening nothing or spinning forever.
  if (url != null && url !== "") {
    throw new ApiError(res.status, body, "The invoice download link was not valid.");
  }

  return {
    status: "pending",
    invoiceStatus: body?.invoiceStatus ?? null,
    correlationId: body?.correlationId ?? null,
    message: pendingMessageOf(body),
  };
}

// ---------- Billing ----------

export interface BillingUpdateInput {
  firstName: string;
  lastName: string;
  email: string;
  isCompany: boolean;
  companyName?: string | null;
  vatNumber?: string | null;
  billingCountry?: string | null;
  billingPostcode?: string | null;
  billingAddress1?: string | null;
  billingAddress2?: string | null;
  billingCity?: string | null;
  billingRegion?: string | null;
}

export async function updateBilling(
  orderKey: string,
  body: BillingUpdateInput,
  authToken?: string,
): Promise<PublicOrder> {
  // Backend contract: { buyer: {...} }. Strip nulls so we only send populated fields.
  const buyer: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(body)) {
    if (v !== null && v !== undefined && v !== "") buyer[k] = v;
  }
  // Always include isCompany even if false
  buyer.isCompany = Boolean(body.isCompany);
  return request<PublicOrder>(`/public/orders/${encodeURIComponent(orderKey)}/billing`, {
    method: "PUT",
    headers: await orderAccessHeaders(authToken),
    body: JSON.stringify({ buyer }),
  });
}

// ---------- Invoice generation ----------

export interface InvoiceGenerateResult {
  status: "ready" | "pending";
  downloadUrl?: string | null;
  invoiceStatus?: string | null;
  correlationId?: string | null;
}

export async function generateInvoice(
  orderKey: string,
  authToken?: string,
): Promise<InvoiceGenerateResult> {
  assertCheckoutMutationAllowed("POST /public/orders/{orderKey}/invoice/generate");
  const res = await fetch(
    `${API_BASE}/public/orders/${encodeURIComponent(orderKey)}/invoice/generate`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(await orderAccessHeaders(authToken)),
      },
      body: JSON.stringify({ confirmBillingAndQuantities: true }),
    },
  );
  const text = await res.text();
  let body: any = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = text; }
  if (res.status === 202) {
    return {
      status: "pending",
      invoiceStatus: body?.invoiceStatus ?? null,
      correlationId: body?.correlationId ?? null,
    };
  }
  if (!res.ok) {
    throw new ApiError(res.status, body, body?.error || body?.message || res.statusText);
  }
  if (body?.downloadUrl || body?.url) {
    return { status: "ready", downloadUrl: body.downloadUrl ?? body.url, correlationId: body?.correlationId ?? null };
  }
  return { status: "pending", invoiceStatus: body?.invoiceStatus ?? null, correlationId: body?.correlationId ?? null };
}

// ---------- Registrations (Slice 3) ----------

export interface AttendeeUpdateInput {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle?: string;
  company?: string;
  countryId?: string | null;
  linkedInProfile?: string;
  dietaryRequirements?: string;
  accessibilityRequirements?: string;
  consentToProcess: boolean;
  consentMarketingEmails: boolean;
  consentShareWithPartners: boolean;
}

export async function getRegistration(
  orderKey: string,
  registrationPublicId: string,
  authToken?: string,
): Promise<RegistrationDetail> {
  return request<RegistrationDetail>(
    `/public/orders/${encodeURIComponent(orderKey)}/registrations/${encodeURIComponent(registrationPublicId)}`,
    {
      headers: await orderAccessHeaders(authToken),
    },
  );
}

export async function updateRegistration(
  orderKey: string,
  registrationPublicId: string,
  body: AttendeeUpdateInput,
  authToken?: string,
): Promise<RegistrationDetail> {
  return request<RegistrationDetail>(
    `/public/orders/${encodeURIComponent(orderKey)}/registrations/${encodeURIComponent(registrationPublicId)}`,
    {
      method: "PUT",
      headers: await orderAccessHeaders(authToken),
      body: JSON.stringify(body),
    },
  );
}

// ---------- Registration options (countries) ----------

let _countriesCache: CountryOption[] | null = null;

export async function getCountryOptions(): Promise<CountryOption[]> {
  if (_countriesCache) return _countriesCache;
  const data = await request<{ countries: CountryOption[] }>("/public/registration-options");
  _countriesCache = Array.isArray(data?.countries) ? data.countries : [];
  return _countriesCache;
}

// ---------- Auth ----------
export async function setOrderPassword(orderKey: string, password: string) {
  return request<{ accessToken: string }>("/public/auth/set-password", {
    method: "POST",
    body: JSON.stringify({ orderKey, password }),
  });
}

export async function getDelegateTokenInfo(token: string) {
  return request<{ eventName: string; firstName: string }>(
    `/public/auth/delegate-token-info?token=${encodeURIComponent(token)}`,
  );
}

export async function setDelegatePassword(delegateToken: string, password: string) {
  return request<{ ok: true; redirectUrl?: string }>("/public/auth/delegate-password", {
    method: "POST",
    body: JSON.stringify({ delegateToken, password }),
  });
}

export const TURNSTILE_SITE_KEY = "0x4AAAAAADFKOeSFZbvr7GrD";
export const SUPPORT_EMAIL = "support@peopleanalyticsworld.com";

// ---------- Preview + Confirm (two-step checkout) ----------

const MOCK_FALLBACK_ENABLED =
  String(import.meta.env.VITE_CHECKOUT_PREVIEW_MOCK_FALLBACK ?? "true").toLowerCase() !== "false";

function isMockPreview(previewVersion: string): boolean {
  return previewVersion.startsWith("mock_");
}

function buildMockPreview(
  input: OrderPreviewInput,
  unitPriceNet: number,
  currency: string,
  productName: string,
  discountAmount: number,
  discountSummary: string | null,
  couponCode: string | null,
): OrderPreviewResponse {
  const qty = Math.max(1, Math.floor(input.lineItem.quantity || 1));
  const subtotalNet = unitPriceNet * qty;
  const discount = Math.min(subtotalNet, Math.max(0, discountAmount || 0));
  const taxableBase = Math.max(0, subtotalNet - discount);
  // Mock: 0% tax. Real tax is calculated by the backend.
  const tax = 0;
  const total = taxableBase + tax;
  return {
    orderKey: input.orderKey,
    previewVersion: `mock_${Date.now()}`,
    currency,
    lineItems: [
      {
        name: productName,
        quantity: qty,
        unitPriceNet,
        subtotalNet,
        discount,
        tax,
        total,
        currency,
      },
    ],
    subtotal: subtotalNet,
    discount,
    tax,
    total,
    billing: {
      firstName: input.buyer.firstName,
      lastName: input.buyer.lastName,
      email: input.buyer.email,
      isCompany: input.buyer.isCompany,
      companyName: input.buyer.companyName ?? null,
      vatNumber: input.buyer.vatNumber ?? null,
      billingCountry: input.buyer.billingCountry,
      billingPostcode: input.buyer.billingPostcode ?? null,
      billingAddress1: input.buyer.billingAddress1 ?? null,
      billingAddress2: input.buyer.billingAddress2 ?? null,
      billingCity: input.buyer.billingCity ?? null,
      billingRegion: input.buyer.billingRegion ?? null,
    },
    coupon: couponCode ? { code: couponCode, summary: discountSummary } : null,
    _mock: true,
  };
}

export interface PreviewOrderMockHints {
  unitPriceNet: number;
  currency: string;
  productName: string;
  discountAmount?: number;
  discountSummary?: string | null;
}

export async function previewOrder(
  input: OrderPreviewInput,
  mockHints?: PreviewOrderMockHints,
): Promise<OrderPreviewResponse> {
  try {
    return await request<OrderPreviewResponse>("/public/orders/preview", {
      method: "POST",
      body: JSON.stringify(input),
    });
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 501) && MOCK_FALLBACK_ENABLED && mockHints) {
      // eslint-disable-next-line no-console
      console.warn("[checkout] preview endpoint unavailable; using local mock preview", { status: e.status });
      return buildMockPreview(
        input,
        mockHints.unitPriceNet,
        mockHints.currency,
        mockHints.productName,
        mockHints.discountAmount ?? 0,
        mockHints.discountSummary ?? null,
        input.couponCode ?? null,
      );
    }
    throw e;
  }
}

export async function confirmOrder(
  orderKey: string,
  previewVersion: string,
  fallbackCreateInput?: CreateOrderInput,
  authToken?: string,
): Promise<OrderConfirmResponse> {
  if (isMockPreview(previewVersion) && MOCK_FALLBACK_ENABLED && fallbackCreateInput) {
    // eslint-disable-next-line no-console
    console.warn("[checkout] confirm via legacy createOrder fallback (mock preview)");
    const r = await createOrder(fallbackCreateInput);
    return { orderKey: r.orderKey, orderId: r.orderId };
  }
  return request<OrderConfirmResponse>(
    `/public/orders/${encodeURIComponent(orderKey)}/confirm`,
    {
      method: "POST",
      headers: await orderAccessHeaders(authToken),
      body: JSON.stringify({ previewVersion, confirmBillingAndQuantities: true }),
    },
  );
}

export const CHECKOUT_PREVIEW_CONFIRM_ENABLED =
  String(import.meta.env.VITE_CHECKOUT_PREVIEW_CONFIRM ?? "true").toLowerCase() !== "false";
